import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Constants
import { loadingRecipe } from "@/_constants/recipe";

// Services and interfaces
import showToast from "@/_lib/toast";
import { ApiError } from "@/interfaces/api/error";
import { IRecipe } from "@/interfaces/recipe/recipe";
import { RecipeFilter } from "@/interfaces/recipe/recipe_builder";
import { useAddRecipeMutation, useGetRecipesQuery } from "@/repositories/recipe";

// Styles
import { Add } from "@carbon/icons-react";

// Components
import GridList from "@/components/grid";
import { TagProps } from "@/components/tag";
import RecipeModal from "./recipes/recipe_modal";
import { Box, Container, Grid } from "@mui/material";
import useRecipeFilterItems from "./recipes/filter_items";
import SecondaryToolbar from "@/components/secondary_toolbar";
import RecipeCard from "@/components/recipe_builder/recipe_card";
import useFilterActions from "@/components/grid/use_filter_actions";


const RecipeListPage: FC = () => {

    const { t } = useTranslation();
    const navigate = useNavigate();

    const filterItems = useRecipeFilterItems();
    // const hasTeam = useSelector(selectHasTeam);
    const [open, setOpen] = useState<boolean>(false);
    const [filterQuery, setFilterQuery] = useState<string>('');
    const [tagFilter, setTagFilter] = useState<RecipeFilter>('all');
    const { filters, setFilter, resetFilters } = useFilterActions();

    const { data: list, isLoading } = useGetRecipesQuery();
    const [addRecipe, { isLoading: isAddingRecipe }] = useAddRecipeMutation();

    const filteredRecipes = useMemo(() => {
        const searchWord = filterQuery.toLocaleLowerCase();
        let filteredItems = list?.filter(recipe => recipe.title.toLocaleLowerCase().includes(searchWord)) ?? [];

        filteredItems = filteredItems.filter(recipe =>
            Object.entries(filters).every(([field, filterValues]) => {
                const recipeValue = recipe[field as keyof IRecipe];
                if (Array.isArray(recipeValue) && field == 'allergens') {
                    console.log('Filtering allergens', field, recipeValue, filterValues);
                    return filterValues.length === 0 || !filterValues.some(val => (recipeValue as string[]).includes(val));
                } else if (Array.isArray(recipeValue)) {
                    return filterValues.length === 0 || filterValues.some(val => (recipeValue as string[]).includes(val));
                }
                return recipeValue !== undefined && (filterValues.length === 0 || filterValues.includes(String(recipeValue)));
            })
        );

        if (tagFilter === 'published') {
            filteredItems = filteredItems.filter(recipe => recipe.published);
        } else if (tagFilter === 'unpublished') {
            filteredItems = filteredItems.filter(recipe => !recipe.published);
        }

        return filteredItems;
    }, [list, filterQuery, filters, tagFilter]);

    const isEmpty = filteredRecipes.length === 0 && !isLoading;
    const hasSearchTerm = filterQuery.trim().length > 0;

    const filterTags: TagProps[] = [
        {
            size: 'medium',
            label: t('components.tag.labels.all'),
            showActiveTick: true,
            active: tagFilter === 'all',
            onSelect: () => setTagFilter('all')
        },
        {
            size: 'medium',
            label: t('general.published'),
            showActiveTick: true,
            active: tagFilter === 'published',
            onSelect: () => setTagFilter('published')
        },
        {
            size: 'medium',
            label: t('general.unpublished'),
            showActiveTick: true,
            active: tagFilter === 'unpublished',
            onSelect: () => setTagFilter('unpublished')
        },
    ];

    const submitAddRecipe = (data: IRecipe) => {
        addRecipe(data).unwrap().then((recipe) => {
            showToast({
                type: 'success', 
                title: t('notifications.recipe.created.title'), 
                message: t('notifications.recipe.created.message', {name: recipe.title})
            });
            setOpen(false);
            navigate(`/library/recipes/${recipe.id}`)
        }).catch((error: ApiError) => {
            showToast({type: 'error', apiError: error.type})
        });
    }

    return (
        <Box display="flex" flexDirection="column" height="calc(100vh - 40px)" sx={{overflow: 'hidden'}}>

            <SecondaryToolbar 
                title={t('pages.library.recipes.title')}
                action1={{
                    label: t('components.buttons.addRecipe'),
                    icon: <Add />,
                    onClick: () => setOpen(true)
                }}
                />
            
                {open && <RecipeModal
                    open={open}
                    isLoading={isAddingRecipe}
                    onClose={() => setOpen(false)}
                    onSave={submitAddRecipe}
                    />}
            
            <Box height='100%' overflow='auto'>
                <Container sx={{padding: '24px 0'}}>
                    <GridList
                        filters={{filterItems, filters, setFilter, resetFilters}}
                        tags={filterTags}
                        onSearchUpdate={setFilterQuery}
                        >
                        {isLoading && Array.from({length: 12}).map((_, i) => (
                            <Grid item key={i} xs={12} sm={6} md={4} lg={2.4} xl={2}>
                                <RecipeCard
                                    recipe={loadingRecipe}
                                    isLoading
                                    />
                            </Grid>
                        ))}

                        {!isLoading && filteredRecipes.map((recipe) => (
                            <Grid item key={recipe.id} xs={12} sm={6} md={4} lg={2.4} xl={2}>
                                <RecipeCard
                                    recipe={recipe}
                                    isLoading={isLoading}
                                    />
                            </Grid>
                        ))}

                        {isEmpty && (
                            <Box display="flex" height="10vh" width="100%" justifyContent="center" alignItems="center">
                                <Box textAlign="center">
                                    {isEmpty && hasSearchTerm && <div className="body-02">{t('components.recipeBuilder.noRecipesFound')}</div>}
                                    {isEmpty && !hasSearchTerm && <div className="body-02">{t('components.recipeBuilder.noRecipes')}</div>}
                                </Box>
                            </Box>
                        )}

                    </GridList>
                </Container>
            </Box>
            
        </Box>
    )
}

export default RecipeListPage